import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

interface Campaign {
  _id: string
  subject: string
  sentCount: number
  openCount: number
  clickCount: number
  sentAt: string
}

interface AnalyticsEvent {
  campaignId: string
  subscriberEmail: string
  eventType: 'open' | 'click'
  timestamp: string
}

interface CohortAnalysisTabProps {
  campaigns: Campaign[]
  analytics: AnalyticsEvent[]
}

function getCohortRetentionMatrix() {
  return [
    { cohort: 'Jan 2025', size: 1200, retention: [100, 85, 72, 65, 58] },
    { cohort: 'Dec 2024', size: 1100, retention: [100, 82, 68, 60, 52] },
    { cohort: 'Nov 2024', size: 950, retention: [100, 78, 65, 55, 48] },
    { cohort: 'Oct 2024', size: 890, retention: [100, 75, 62, 52, 45] }
  ]
}

export default function CohortAnalysisTab({ campaigns, analytics }: CohortAnalysisTabProps) {
  const getCohortData = () => {
    const cohorts: Record<string, Record<string, number>> = {}
    
    campaigns.forEach(campaign => {
      const cohortMonth = new Date(campaign.sentAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
      if (!cohorts[cohortMonth]) {
        cohorts[cohortMonth] = { sent: 0, opened: 0, clicked: 0 }
      }
      cohorts[cohortMonth].sent += campaign.sentCount
      cohorts[cohortMonth].opened += campaign.openCount
      cohorts[cohortMonth].clicked += campaign.clickCount
    })

    return Object.entries(cohorts).map(([month, data]) => ({
      month,
      sent: data.sent,
      opened: data.opened,
      clicked: data.clicked,
      openRate: data.sent > 0 ? ((data.opened / data.sent) * 100).toFixed(1) : 0,
      clickRate: data.opened > 0 ? ((data.clicked / data.opened) * 100).toFixed(1) : 0,
      retention: data.sent > 0 ? ((data.clicked / data.sent) * 100).toFixed(1) : 0
    }))
  }

  return (
    <div className="space-y-8">
      {/* Cohort Retention Matrix */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Cohort Retention Matrix</h2>
        <p className="text-sm text-gray-600 mb-4">Subscriber retention rates by acquisition month</p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cohort</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month 0</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month 1</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month 2</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month 3</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Month 6</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getCohortRetentionMatrix().map((cohort, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{cohort.cohort}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{cohort.size}</td>
                  {cohort.retention.map((rate, rIdx) => (
                    <td key={rIdx} className="px-4 py-3 text-center">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          rate >= 50 ? 'bg-green-100 text-green-800' :
                          rate >= 30 ? 'bg-yellow-100 text-yellow-800' :
                          rate >= 10 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}
                      >
                        {rate}%
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cohort Engagement Trends */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Cohort Engagement Over Time</h2>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={getCohortData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="openRate" stroke="#10b981" strokeWidth={2} />
            <Line type="monotone" dataKey="clickRate" stroke="#3b82f6" strokeWidth={2} />
            <Line type="monotone" dataKey="retention" stroke="#f59e0b" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
